import { create } from "zustand";
import { format } from "date-fns";
import type { PosBloqueo, PosEstado } from "@/platform/types";
import { messageForUser } from "@/utils/errorMapping";
import type { SituacionDelEquipo } from "./situacionDelEquipo";

/**
 * Lo que dice el latido de la caja (trd-client#344 AC-2), del lado de la
 * pantalla. El latido lo manda Rust; acá se guarda el último estado que mandó.
 */

export const ESTADO_INICIAL: PosEstado = {
  conexion: "desconocida",
  bloqueo: null,
  terminalId: null,
  sinConexionDesde: null,
  ultimoLatidoEn: null,
  pendientes: 0,
  mandatoId: null,
  razonSinMandato: null,
  servidorConFoto: null,
};

/**
 * El último estado del latido. Vive fuera de React: la barra superior se
 * vuelve a montar con cada ruta, y el latido no.
 */
export const useEstadoDeLaCaja = create<PosEstado>(() => ESTADO_INICIAL);

export const SIN_CONEXION =
  "Sin conexión: no se puede vender hasta que vuelva la red. Lo que ya está en la cola sube solo.";

/** Por qué la caja no vende, en palabras del mostrador. */
export function motivoDelBloqueo(bloqueo: PosBloqueo): string {
  switch (bloqueo.tipo) {
    case "dada-de-baja":
      return "Esta caja está dada de baja: no vende.";
    case "suspendido":
      return "El negocio está suspendido: la caja no vende.";
    case "sin-permiso":
      // §2.7 de la ronda 2: los demás cajeros siguen con su PIN.
      return "Este usuario no puede vender.";
    case "no-es-esta-caja":
      return "Este equipo no está dado de alta como esta caja.";
    case "sesion":
      return "Iniciá sesión para subir las ventas.";
    case "rechazado":
      // El mensaje del servidor, o el del código si lo conocemos.
      return messageForUser(
        {
          response: {
            data: {
              errorCode: bloqueo.codigo ?? undefined,
              message: bloqueo.mensaje,
            },
          },
        },
        `El servidor no deja vender con esta caja (HTTP ${bloqueo.estado}).`
      );
  }
}

export type Tono = "ok" | "aviso" | "error" | "neutro";

export interface Indicador {
  etiqueta: string;
  tono: Tono;
  /** Para el tooltip: desde cuándo, o por qué. */
  detalle?: string;
}

const ETIQUETA_DEL_BLOQUEO: Record<PosBloqueo["tipo"], string> = {
  "dada-de-baja": "Caja dada de baja",
  suspendido: "Negocio suspendido",
  "sin-permiso": "Sin permiso para vender",
  "no-es-esta-caja": "No es esta caja",
  sesion: "Iniciá sesión",
  rechazado: "La caja no vende",
};

/** La hora del reloj del equipo, la que ve el cajero: `13:42`. */
const horaCorta = (iso: string) => {
  const fecha = new Date(iso);
  return Number.isNaN(fecha.getTime()) ? iso : format(fecha, "HH:mm");
};

/**
 * El indicador de la barra: conectado, con problemas o sin conexión (AC-2).
 * Un bloqueo le gana a la conexión: un negocio suspendido no es «sin red».
 * Sin terminal, no hay indicador.
 */
export function indicadorDeLaCaja(estado: PosEstado): Indicador | null {
  if (!estado.terminalId) return null;
  if (estado.bloqueo) {
    return {
      etiqueta: ETIQUETA_DEL_BLOQUEO[estado.bloqueo.tipo],
      tono: "error",
      detalle: motivoDelBloqueo(estado.bloqueo),
    };
  }
  switch (estado.conexion) {
    case "conectada":
      return { etiqueta: "Conectado", tono: "ok" };
    case "con-problemas":
      return {
        etiqueta: "Con problemas",
        tono: "aviso",
        detalle: "La conexión anda lenta o perdió un latido. Se puede vender.",
      };
    case "sin-conexion":
      return {
        etiqueta: "Sin conexión",
        tono: "error",
        detalle: estado.sinConexionDesde
          ? `Sin conexión desde las ${horaCorta(estado.sinConexionDesde)}.`
          : undefined,
      };
    case "desconocida":
      return { etiqueta: "Conectando…", tono: "neutro" };
  }
}

export type PuedeVender = { puede: true } | { puede: false; motivo: string };

const PUEDE: PuedeVender = { puede: true };

/**
 * ¿Puede vender el mostrador? (AC-2: las acciones que necesitan red quedan
 * bloqueadas con un mensaje claro.) Sólo una caja de este local se bloquea: un
 * equipo sin alta, o la caja de otro local, vende online como la web. En la
 * ronda 1 no se vende sin red (§0 del contrato).
 */
export function puedeVender(
  estado: PosEstado,
  situacion: SituacionDelEquipo
): PuedeVender {
  if (situacion.tipo === "dada-de-baja") {
    return { puede: false, motivo: motivoDelBloqueo({ tipo: "dada-de-baja" }) };
  }
  if (situacion.tipo !== "caja") return PUEDE;
  if (estado.bloqueo) {
    return { puede: false, motivo: motivoDelBloqueo(estado.bloqueo) };
  }
  if (estado.conexion === "sin-conexion") {
    return { puede: false, motivo: SIN_CONEXION };
  }
  return PUEDE;
}
